import { createReduxHistoryContext } from 'redux-first-history'
import { createBrowserHistory, createMemoryHistory } from 'history'
import { configureStore } from '@reduxjs/toolkit'
import { combineReducers } from 'redux'
import * as reducers from './ducks'
import { httpUtil } from '../utils/http'

const isServer = typeof module !== 'undefined' && module.exports

interface StoreConfig {
    consoleAPIURL?: string
    urlPath?: string
    services?: Record<string, any>
    // Extra middleware, supplied by the caller. The development logger arrives this way
    // for the reason docs/REVIEW.md, Module System, gives.
    middleware?: any[]
}

const setupStore = (initialState: Record<string, any> = {}, config: StoreConfig = {}) => {
    const urlPath = config.urlPath || '/'
    const {
        routerMiddleware,
        createReduxHistory,
        routerReducer
    } = createReduxHistoryContext({
        history: isServer ? createMemoryHistory({ initialEntries: [ urlPath ] }) : createBrowserHistory()
    })
    const services = {
        dataAPI: (endpoint: string, options?: Record<string, any>) => httpUtil(
            `${config.consoleAPIURL}${endpoint}`,
            options,
        )
        .catch((err: any) => {
            if (err.status === 401) {
                window.location.href = '/'
                return
            }
            throw err
        }),
        ...(config.services ? config.services : {})
    }
    const store = configureStore({
        reducer: combineReducers({
            router: routerReducer,
            ...reducers,
            user: (state = {}) => state,
        }),
        preloadedState: initialState,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware({
            serializableCheck: false,
            thunk: {
                extraArgument: services
            }
        }).concat(
            routerMiddleware,
            ...(config.middleware || []),
        )
    })
    if (!isServer && process.env.NODE_ENV === 'development') {
        (window as any).store = store
    }
    const history = createReduxHistory(store)
    return {
        history,
        store,
    }
}

export default setupStore
